/**
 * Audio Recorder Hook
 * Custom hook voor audio opname met pause/resume functionaliteit.
 * Ondersteunt callbacks voor blob-gereedheid, fouten en zichtbaarheidswijzigingen.
 */

import { useState, useRef, useCallback } from 'react';

interface AudioRecorderOptions {
  /** Wordt aangeroepen zodra de blob klaar is, vóór de state-update */
  onBlobReady?: (blob: Blob) => void;
  /** Wordt aangeroepen bij een MediaRecorder-fout */
  onError?: (message: string) => void;
  /** Wordt aangeroepen als de opname automatisch gepauzeerd wordt (achtergrond/telefoongesprek) */
  onPausedByVisibility?: () => void;
}

export const useAudioRecorder = (options?: AudioRecorderOptions) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const totalPausedDurationRef = useRef<number>(0);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerIntervalRef.current = window.setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - startTimeRef.current - totalPausedDurationRef.current) / 1000
      );
      setRecordingDuration(elapsed);
    }, 1000);
  }, [stopTimer]);

  const removeVisibilityListener = useCallback(() => {
    if (visibilityHandlerRef.current) {
      document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      visibilityHandlerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      console.log('🎤 Requesting microphone access...');

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log('✓ Microphone access granted');

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('📦 Audio chunk received:', event.data.size, 'bytes');
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log('🛑 MediaRecorder stopped');
        console.log('📊 Total chunks collected:', chunksRef.current.length);

        removeVisibilityListener();

        const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        console.log('✓ Created final blob:', blob.size, 'bytes');

        // Callback vóór state-update zodat de caller de blob kan opslaan
        options?.onBlobReady?.(blob);

        setAudioBlob(blob);

        stream.getTracks().forEach((track) => track.stop());
        console.log('✓ Microphone released');
      };

      mediaRecorder.onpause = () => {
        console.log('⏸️ MediaRecorder paused');
      };

      mediaRecorder.onresume = () => {
        console.log('▶️ MediaRecorder resumed');
      };

      // Foutafhandeling: bewaar verzamelde chunks en informeer de gebruiker
      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event);
        removeVisibilityListener();
        stopTimer();
        options?.onError?.(
          'Opname onderbroken door een fout. Probeer opnieuw op te nemen.'
        );
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Verzamel data elke seconde

      // Automatisch pauzeren bij achtergrond (telefoongesprek, tab-wissel op mobiel)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          const rec = mediaRecorderRef.current;
          if (rec && rec.state === 'recording') {
            try {
              rec.pause();
              pausedTimeRef.current = Date.now();
              setIsPaused(true);
              stopTimer();
              options?.onPausedByVisibility?.();
              console.log('⏸️ Opname automatisch gepauzeerd (achtergrond)');
            } catch (e) {
              // iOS Safari gooit soms een fout bij pause() — negeer dit
              console.warn('Pause bij visibilitychange mislukt (mogelijk iOS):', e);
            }
          }
        }
      };

      visibilityHandlerRef.current = handleVisibilityChange;
      document.addEventListener('visibilitychange', handleVisibilityChange);

      setIsRecording(true);
      setIsPaused(false);
      startTimeRef.current = Date.now();
      totalPausedDurationRef.current = 0;

      startTimer();
      console.log('✓ Recording started');
    } catch (error) {
      console.error('❌ Microphone access error:', error);
      throw new Error('Microfoon toegang geweigerd. Controleer browser instellingen.');
    }
  }, [options, startTimer, stopTimer, removeVisibilityListener]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('⏸️ Pausing recording...');
      mediaRecorderRef.current.pause();
      pausedTimeRef.current = Date.now();
      setIsPaused(true);
      stopTimer();
      console.log('✓ Recording paused');
    }
  }, [stopTimer]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      console.log('▶️ Resuming recording...');
      mediaRecorderRef.current.resume();
      const pauseDuration = Date.now() - pausedTimeRef.current;
      totalPausedDurationRef.current += pauseDuration;
      setIsPaused(false);
      startTimer();
      console.log('✓ Recording resumed');
    }
  }, [startTimer]);

  const stopRecording = useCallback(async () => {
    console.log('🛑 Stopping recording...');
    stopTimer();

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      if (mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      console.log('✓ Stop signal sent to MediaRecorder');
    }
  }, [stopTimer]);

  const resetRecording = useCallback(() => {
    console.log('🔄 Resetting recording state...');
    removeVisibilityListener();
    stopTimer();
    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
    setAudioBlob(null);
    chunksRef.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;
    totalPausedDurationRef.current = 0;
    console.log('✓ Recording state reset');
  }, [stopTimer, removeVisibilityListener]);

  return {
    isRecording,
    isPaused,
    recordingDuration,
    audioBlob,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
  };
};
